import React, { useEffect, useState, useCallback, useMemo } from "react";
import Navbar from "@/components/Navbar";
import MapView from "@/components/MapView";
import { api, GEORGETOWN, formatGYD } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  MapPin,
  Flag,
  CurrencyDollar,
  CarProfile,
  Star,
  Phone,
  XCircle,
  CheckCircle,
  BellRinging,
  BellSlash,
} from "@phosphor-icons/react";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const PRESETS = [
  { label: "Georgetown", lat: 6.8013, lng: -58.1551 },
  { label: "New Amsterdam", lat: 6.2486, lng: -57.5239 },
  { label: "Cheddi Jagan Intl Airport", lat: 6.4986, lng: -58.2540 },
  { label: "Giftland Mall", lat: 6.8033, lng: -58.1188 },
  { label: "Ogle Airport", lat: 6.8060, lng: -58.1107 },
  { label: "Linden", lat: 6.0083, lng: -58.3033 },
];

function StatPill({ label, value, testId }) {
  return (
    <div className="border-2 border-foreground p-3 bg-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60">{label}</div>
      <div className="font-display text-xl" data-testid={testId}>{value}</div>
    </div>
  );
}

export default function RiderDashboard() {
  const { user } = useAuth();
  const [pickup, setPickup] = useState({ ...GEORGETOWN, address: "Georgetown" });
  const [dropoff, setDropoff] = useState(null);
  const [pickMode, setPickMode] = useState("dropoff"); // which one map clicks set
  const [estimate, setEstimate] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [ride, setRide] = useState(null);
  const push = usePushNotifications();
  const [driverLive, setDriverLive] = useState(null); // {location, last_seen}
  // Rolling breadcrumb trail of the driver's last ~12 positions, so the rider
  // sees a fading polyline of the driver approaching. Reset between rides.
  const [driverTrail, setDriverTrail] = useState([]);
  const [driverEta, setDriverEta] = useState(null); // {eta_min, distance_km, source}
  const [recenter, setRecenter] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [routeCoords, setRouteCoords] = useState(null);
  const [geocoding, setGeocoding] = useState({ pickup: false, dropoff: false });
  const [communityPins, setCommunityPins] = useState([]);

  // Fetch community pins every 60s so police/hazard alerts surface on the
  // booking map without a refresh.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/community/posts?limit=80");
        if (!cancelled) setCommunityPins(data || []);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Persist the last typed/tapped pickup so the WhatsApp-driver message
  // (on the public driver profile) can include it as context.
  useEffect(() => {
    try {
      if (pickup?.address && !pickup.address.startsWith("Pin ·")) {
        sessionStorage.setItem("ridegy:last_pickup", pickup.address);
      }
    } catch { /* private mode */ }
  }, [pickup?.address]);

  // Active ride polling
  const loadActive = useCallback(async () => {
    try {
      const { data } = await api.get("/rides/active");
      setRide(data || null);
      if (data) {
        setPickup(data.pickup);
        setDropoff(data.dropoff);
      }
    } catch {}
  }, []);

  useEffect(() => { loadActive(); }, [loadActive]);

  useEffect(() => {
    if (!ride || ["completed", "cancelled"].includes(ride.status)) return;
    const t = setInterval(loadActive, 4000);
    return () => clearInterval(t);
  }, [ride, loadActive]);

  // Real-time driver location polling while the ride is in flight
  useEffect(() => {
    if (!ride || !ride.driver_id || !["accepted", "in_progress"].includes(ride.status)) {
      setDriverLive(null);
      setDriverEta(null);
      setDriverTrail([]);
      return;
    }
    let cancelled = false;
    const fetchLoc = async () => {
      try {
        const { data } = await api.get(`/rides/${ride.ride_id}/driver-location`);
        if (!cancelled) {
          setDriverLive(data);
          // On the FIRST poll for this ride, seed the trail from the
          // server-persisted history so a refresh restores the rider's view.
          // After that, merge new pings into the local trail (deduped at ~10m).
          if (data?.location?.lat != null && data?.location?.lng != null) {
            setDriverTrail((prev) => {
              if (prev.length === 0 && Array.isArray(data.trail) && data.trail.length > 0) {
                return data.trail;
              }
              const last = prev[prev.length - 1];
              if (last) {
                const dy = (data.location.lat - last[0]) * 111320;
                const dx = (data.location.lng - last[1]) * 111320 * Math.cos((data.location.lat * Math.PI) / 180);
                if (Math.sqrt(dx * dx + dy * dy) < 10) return prev;
              }
              const next = [...prev, [data.location.lat, data.location.lng]];
              return next.length > 12 ? next.slice(next.length - 12) : next;
            });
          }
        }
      } catch {}
    };
    const fetchEta = async () => {
      try {
        const { data } = await api.get(`/rides/${ride.ride_id}/eta`);
        if (!cancelled) setDriverEta(data);
      } catch {}
    };
    fetchLoc();
    fetchEta();
    const tLoc = setInterval(fetchLoc, 3000);
    const tEta = setInterval(fetchEta, 15000); // poll ETA every 15s (server caches 20s)
    return () => { cancelled = true; clearInterval(tLoc); clearInterval(tEta); };
  }, [ride]);

  // Nearby drivers near pickup
  useEffect(() => {
    if (!pickup || ride) return;
    let cancelled = false;
    const fetchDrivers = async () => {
      try {
        const { data } = await api.get("/drivers/nearby", { params: { lat: pickup.lat, lng: pickup.lng } });
        if (!cancelled) setDrivers(data);
      } catch {}
    };
    fetchDrivers();
    const t = setInterval(fetchDrivers, 7000);
    return () => { cancelled = true; clearInterval(t); };
  }, [pickup, ride]);

  // Auto estimate when both set
  useEffect(() => {
    if (!pickup || !dropoff || ride) { setEstimate(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/rides/estimate", { pickup, dropoff });
        if (!cancelled) setEstimate(data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [pickup, dropoff, ride]);

  const onMapClick = (pt) => {
    if (ride) return;
    if (pickMode === "pickup") {
      setPickup({ ...pt, address: `Pin · ${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}` });
      setPickMode("dropoff");
    } else {
      setDropoff({ ...pt, address: `Pin · ${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}` });
    }
  };

  // Free-text → coordinates via Nominatim (Guyana-biased). Triggered by Enter / blur.
  const geocodeAddress = async (which, text) => {
    const q = (text || "").trim();
    if (!q || q.startsWith("Pin ·")) return;
    setGeocoding((g) => ({ ...g, [which]: true }));
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=gy&limit=1`;
      const r = await fetch(url, { headers: { Accept: "application/json" } });
      const data = await r.json();
      if (!data.length) {
        toast.error(`Couldn't find "${q}" in Guyana`);
        return;
      }
      const hit = {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        address: data[0].display_name?.split(",").slice(0, 2).join(", ") || q,
      };
      if (which === "pickup") setPickup(hit);
      else setDropoff(hit);
      setRecenter(hit);
    } catch {
      toast.error("Search failed — check your connection");
    } finally {
      setGeocoding((g) => ({ ...g, [which]: false }));
    }
  };

  // Fetch the real road route (OSRM) whenever both endpoints are set.
  useEffect(() => {
    if (!pickup || !dropoff) { setRouteCoords(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${dropoff.lng},${dropoff.lat}?overview=full&geometries=geojson`;
        const r = await fetch(url);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const geom = data?.routes?.[0]?.geometry?.coordinates;
        if (Array.isArray(geom)) setRouteCoords(geom.map(([lng, lat]) => [lat, lng]));
      } catch { /* silent — straight-line fallback already drawn */ }
    })();
    return () => { cancelled = true; };
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng]);

  const applyPreset = (p, target) => {
    const v = { lat: p.lat, lng: p.lng, address: p.label };
    if (target === "pickup") setPickup(v);
    else setDropoff(v);
    setRecenter(v);
  };

  const requestRide = async () => {
    if (!pickup || !dropoff) { toast.error("Pick both pickup and drop-off"); return; }
    try {
      const { data } = await api.post("/rides/request", { pickup, dropoff });
      setRide(data);
      toast.success("Ride requested — finding a driver…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to request ride");
    }
  };

  const cancelRide = async () => {
    if (!ride) return;
    try {
      await api.post(`/rides/${ride.ride_id}/cancel`);
      setRide(null);
      setDropoff(null);
      toast.success("Ride cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    }
  };

  const submitRating = async () => {
    if (!ride || !rating) return;
    try {
      await api.post(`/rides/${ride.ride_id}/rate`, { rating, review });
      toast.success("Thanks for rating!");
      setRide(null);
      setDropoff(null);
      setRating(0);
      setReview("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rate");
    }
  };

  const statusLabel = useMemo(() => {
    if (!ride) return null;
    return {
      requested: "Finding your driver…",
      accepted: "Driver on the way",
      in_progress: "On your trip",
      completed: "Trip complete",
      cancelled: "Cancelled",
    }[ride.status];
  }, [ride]);

  return (
    <div className="h-screen flex flex-col">
      <Navbar active="ride" />
      <div className="flex-1 grid grid-cols-1 grid-rows-[55vh_1fr] md:grid-rows-1 md:grid-cols-[400px_1fr] min-h-0">
        {/* Booking panel */}
        <aside className="order-2 md:order-1 border-t-2 md:border-t-0 md:border-r-2 border-foreground bg-card overflow-y-auto" data-testid="rider-booking-panel">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">Booking</div>
              <span className="inline-flex items-center gap-1 bg-primary border-2 border-foreground px-2 py-1 font-mono text-[10px] uppercase tracking-wider">
                <CurrencyDollar size={12} weight="bold" /> Cash only
              </span>
            </div>

            {push.supported && !push.subscribed && (
              <div
                className="border-2 border-foreground bg-secondary p-3 mb-4 flex items-center gap-3"
                data-testid="rider-push-banner"
              >
                <BellRinging size={18} weight="fill" className="shrink-0 text-accent" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                    Ride alerts
                  </div>
                  <div className="text-sm">
                    Get pinged when a driver accepts, arrives, and finishes your ride.
                  </div>
                </div>
                <Button
                  data-testid="rider-push-enable"
                  onClick={async () => {
                    const ok = await push.enable();
                    if (ok) toast.success("Notifications enabled");
                    else if (push.error) toast.error(push.error);
                  }}
                  disabled={push.busy}
                  className="h-9 rounded-none border-2 border-foreground bg-foreground text-background hover:bg-foreground/90"
                >
                  {push.busy ? "…" : "Enable"}
                </Button>
              </div>
            )}
            {push.subscribed && (
              <div
                className="flex items-center justify-between mb-3 font-mono text-[10px] uppercase tracking-wider text-foreground/60"
                data-testid="rider-push-status"
              >
                <span className="inline-flex items-center gap-1">
                  <BellRinging size={12} weight="fill" className="text-accent" />
                  Ride alerts on
                </span>
                <button
                  type="button"
                  onClick={() => push.disable()}
                  data-testid="rider-push-disable"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  <BellSlash size={12} /> Off
                </button>
              </div>
            )}

            {!ride && (
              <>
                <h2 className="font-display text-3xl tracking-tighter mb-6">Where to?</h2>

                <div className="space-y-4">
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-wider flex items-center gap-2">
                      <span className="inline-block w-4 h-4 bg-primary border border-foreground" /> Pickup
                    </Label>
                    <Input
                      data-testid="rider-pickup-input"
                      value={pickup?.address || ""}
                      onChange={(e) => setPickup({ ...(pickup || GEORGETOWN), address: e.target.value })}
                      onFocus={() => setPickMode("pickup")}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); geocodeAddress("pickup", e.target.value); } }}
                      onBlur={(e) => geocodeAddress("pickup", e.target.value)}
                      placeholder="Type an address or tap the map"
                      className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-wider flex items-center gap-2">
                      <span className="inline-block w-4 h-4 bg-foreground" /> Drop-off
                    </Label>
                    <Input
                      data-testid="rider-dropoff-input"
                      value={dropoff?.address || ""}
                      onChange={(e) => setDropoff({ ...(dropoff || GEORGETOWN), address: e.target.value })}
                      onFocus={() => setPickMode("dropoff")}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); geocodeAddress("dropoff", e.target.value); } }}
                      onBlur={(e) => geocodeAddress("dropoff", e.target.value)}
                      placeholder="Type an address or tap the map"
                      className="rounded-none mt-1 border-2 border-foreground/30 h-11"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-2">
                    Tap a preset · sets {pickMode}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {PRESETS.map((p) => (
                      <button
                        key={p.label}
                        data-testid={`rider-preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => applyPreset(p, pickMode)}
                        className="text-left px-3 py-2 border-2 border-foreground/20 hover:border-foreground text-sm bg-background"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {estimate && (
                  <div className="mt-6 border-2 border-foreground p-4 bg-background">
                    <div className="grid grid-cols-3 gap-2">
                      <StatPill label="Distance" value={`${estimate.distance_km} km`} testId="rider-estimate-distance" />
                      <StatPill label="Fare" value={formatGYD(estimate.fare_gyd)} testId="rider-estimate-fare" />
                      <StatPill label="ETA" value={`${estimate.eta_min} min`} testId="rider-estimate-eta" />
                    </div>
                    <Button
                      data-testid="rider-request-ride-btn"
                      onClick={requestRide}
                      className="w-full mt-4 rounded-none h-12 bg-foreground text-background hover:bg-foreground/90 font-semibold"
                    >
                      Request Ride · {formatGYD(estimate.fare_gyd)}
                    </Button>
                  </div>
                )}

                <div className="mt-6 border-t-2 border-foreground/20 pt-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-2">
                    Drivers near you · {drivers.length}
                  </div>
                  <ul className="space-y-1 max-h-40 overflow-auto">
                    {drivers.slice(0, 6).map((d) => (
                      <li key={d.user_id} className="flex items-center justify-between text-sm px-2 py-1 border border-foreground/15">
                        <span className="flex items-center gap-2"><CarProfile size={14} /> {d.vehicle_make} {d.vehicle_model}</span>
                        <span className="font-mono text-xs">{d.distance_km} km</span>
                      </li>
                    ))}
                    {drivers.length === 0 && <li className="text-sm text-foreground/60">No drivers nearby right now.</li>}
                  </ul>
                </div>
              </>
            )}

            {ride && (
              <div data-testid="rider-active-ride-panel">
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-block w-2 h-2 bg-accent pulse-dot rounded-full" />
                  <span className="font-mono text-xs uppercase tracking-wider text-accent" data-testid="rider-ride-status">
                    {statusLabel}
                  </span>
                </div>
                <h2 className="font-display text-3xl tracking-tighter">{formatGYD(ride.fare_gyd)}</h2>
                <div className="font-mono text-xs uppercase tracking-wider text-foreground/60 mt-1">
                  {ride.distance_km} km · Cash on arrival
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} weight="fill" className="text-primary mt-0.5" />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Pickup</div>
                      <div>{ride.pickup.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Flag size={18} weight="fill" className="text-foreground mt-0.5" />
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Drop-off</div>
                      <div>{ride.dropoff.address}</div>
                    </div>
                  </div>
                </div>

                {ride.driver_id && (
                  <div className="mt-6 border-2 border-foreground p-4 bg-background">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Your driver</div>
                      <a
                        data-testid="rider-view-driver-profile"
                        href={`/d/${ride.driver_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-[10px] uppercase tracking-wider underline"
                      >
                        View profile →
                      </a>
                    </div>
                    <div className="font-display text-xl mt-1" data-testid="rider-driver-name">{ride.driver_name}</div>
                    <div className="text-sm text-foreground/70 mt-1">{ride.driver_vehicle}</div>
                    <div className="mt-2 font-mono text-sm border-2 border-foreground inline-block px-2 py-1" data-testid="rider-driver-plate">
                      {ride.driver_plate}
                    </div>
                    {driverLive?.location && (
                      <div className="mt-3 grid grid-cols-2 gap-2 border-t border-foreground/20 pt-3">
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                            {ride.status === "in_progress" ? "Time to drop-off" : "Arrives in"}
                          </div>
                          <div className="font-display text-2xl" data-testid="rider-driver-eta">
                            {driverEta?.eta_min != null ? `~${driverEta.eta_min} min` : "—"}
                          </div>
                          {driverEta?.source && (
                            <div className="font-mono text-[9px] uppercase tracking-wider text-foreground/50 mt-0.5">
                              {driverEta.source === "osrm" ? "Live road · OSRM" : "Estimated"}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Distance</div>
                          <div className="font-display text-2xl" data-testid="rider-driver-distance">
                            {(() => {
                              if (driverEta?.distance_km != null) return `${driverEta.distance_km} km`;
                              const target = ride.status === "in_progress" ? ride.dropoff : ride.pickup;
                              const R = 6371;
                              const p1 = (driverLive.location.lat * Math.PI) / 180;
                              const p2 = (target.lat * Math.PI) / 180;
                              const dp = ((target.lat - driverLive.location.lat) * Math.PI) / 180;
                              const dl = ((target.lng - driverLive.location.lng) * Math.PI) / 180;
                              const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
                              const d = 2 * R * Math.asin(Math.sqrt(a));
                              return `${d.toFixed(2)} km`;
                            })()}
                          </div>
                          <div className="font-mono text-[9px] uppercase tracking-wider text-accent mt-0.5 inline-flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 bg-accent rounded-full pulse-dot" /> Live
                          </div>
                        </div>
                      </div>
                    )}
                    {driverLive?.location && (
                      <div className="mt-3 border-t border-foreground/20 pt-3 flex items-center justify-between gap-2" data-testid="rider-driver-speed-row">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                          Driver speed
                        </div>
                        <div className="font-display text-xl inline-flex items-center gap-2" data-testid="rider-driver-speed">
                          {driverLive.speed_hidden ? (
                            <span className="font-mono text-xs uppercase tracking-wider text-foreground/60">Hidden</span>
                          ) : driverLive.speed_kmh != null && driverLive.speed_kmh > 0 ? (
                            <>
                              <span>{Math.round(driverLive.speed_kmh)}</span>
                              <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">km/h</span>
                              {driverLive.speed_kmh < 10 && (
                                <span className="font-mono text-[9px] uppercase tracking-wider bg-destructive text-destructive-foreground px-1.5 py-0.5 ml-1" title="Slow-crawl detected">
                                  In traffic
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="font-mono text-xs uppercase tracking-wider text-foreground/60">Stopped</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 flex gap-2">
                  {["requested", "accepted"].includes(ride.status) && (
                    <Button
                      data-testid="rider-cancel-btn"
                      variant="outline"
                      onClick={cancelRide}
                      className="rounded-none border-2 border-foreground"
                    >
                      <XCircle size={16} /> Cancel
                    </Button>
                  )}
                </div>

                {ride.status === "completed" && !ride.rating && (
                  <div className="mt-6 border-2 border-foreground p-4">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mb-2">Rate your trip</div>
                    <div className="flex gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          data-testid={`rider-rate-${n}`}
                          onClick={() => setRating(n)}
                          className={`p-1 border-2 ${rating >= n ? "bg-primary border-foreground" : "border-foreground/20"}`}
                        >
                          <Star size={20} weight={rating >= n ? "fill" : "regular"} />
                        </button>
                      ))}
                    </div>
                    <Input
                      data-testid="rider-review-input"
                      value={review}
                      onChange={(e) => setReview(e.target.value)}
                      placeholder="Tell us about the ride (optional)"
                      className="rounded-none border-2 border-foreground/30"
                    />
                    <Button
                      data-testid="rider-submit-rating-btn"
                      onClick={submitRating}
                      disabled={!rating}
                      className="mt-3 rounded-none bg-foreground text-background"
                    >
                      <CheckCircle size={16} /> Submit
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Map */}
        <main className="order-1 md:order-2 relative min-h-0">
          <MapView
            center={pickup || GEORGETOWN}
            recenter={recenter}
            zoom={13}
            pickup={pickup}
            dropoff={dropoff}
            drivers={
              ride
                ? (driverLive?.location
                    ? [{
                        user_id: ride.driver_id || "assigned",
                        location: driverLive.location,
                        vehicle_make: ride.driver_vehicle?.split(" ")?.[1] || "Driver",
                        vehicle_model: ride.driver_vehicle?.split(" ")?.slice(2).join(" ") || "",
                        picture: ride.driver_profile?.picture,
                        initial: ride.driver_profile?.initial || (ride.driver_name?.[0] || "D").toUpperCase(),
                        distance_km: driverEta?.distance_km ?? null,
                        speed_kmh: driverLive?.speed_kmh,
                        speed_hidden: driverLive?.speed_hidden,
                        address: driverLive?.address,
                        trail: driverTrail,
                      }]
                    : [])
                : drivers
            }
            onMapClick={onMapClick}
            showRoute={!!(pickup && dropoff)}
            routeCoords={routeCoords}
            communityPins={communityPins}
            dataTestId="rider-map"
          />
          {!ride && (
            <div className="absolute top-3 left-3 right-3 sm:right-auto z-[400] flex items-center gap-2" data-testid="rider-map-mode-toggle">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-background border-2 border-foreground px-2 py-2 shrink-0">Tap map for</span>
              <button
                type="button"
                data-testid="rider-map-mode-pickup"
                onClick={() => setPickMode("pickup")}
                className={`flex-1 sm:flex-none font-mono text-[10px] uppercase tracking-[0.15em] border-2 border-foreground h-9 px-2 flex items-center gap-1.5 ${pickMode === "pickup" ? "bg-primary text-foreground" : "bg-background hover:bg-secondary"}`}
              >
                <span className="inline-block w-3 h-3 bg-primary border border-foreground" />
                Pickup
              </button>
              <button
                type="button"
                data-testid="rider-map-mode-dropoff"
                onClick={() => setPickMode("dropoff")}
                className={`flex-1 sm:flex-none font-mono text-[10px] uppercase tracking-[0.15em] border-2 border-foreground h-9 px-2 flex items-center gap-1.5 ${pickMode === "dropoff" ? "bg-foreground text-background" : "bg-background hover:bg-secondary"}`}
              >
                <span className={`inline-block w-3 h-3 ${pickMode === "dropoff" ? "bg-background" : "bg-foreground"} border border-foreground`} />
                Drop-off
              </button>
            </div>
          )}
          {ride && driverLive?.location && (
            <div
              data-testid="rider-driver-live-indicator"
              className="absolute top-3 left-3 bg-foreground text-background border-2 border-foreground px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] z-[400] flex items-center gap-2 flex-wrap max-w-[calc(100%-1.5rem)]"
            >
              <span className="inline-block w-2 h-2 bg-accent rounded-full pulse-dot" />
              <span data-testid="rider-driver-live-coords">Driver live · {driverLive.location.lat.toFixed(4)}, {driverLive.location.lng.toFixed(4)}</span>
              {!driverLive.speed_hidden && driverLive.speed_kmh != null && (
                <span className="inline-flex items-center gap-1 border-l border-background/30 pl-2" data-testid="rider-driver-live-speed">
                  {driverLive.speed_kmh > 0 ? `${Math.round(driverLive.speed_kmh)} km/h` : "Stopped"}
                </span>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
